import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  FiCheckCircle,
  FiTrendingUp,
  FiDollarSign,
  FiShoppingCart,
  FiAward,
} from 'react-icons/fi';

const FlippableCard = ({ feature, darkTheme }) => {
  const [flipped, setFlipped] = useState(false);
  
  const handleClick = () => {
    setFlipped((prev) => !prev);
  };

  // Determine the action label based on feature title.
  const actionLabel = feature.title === "Project Tokenization" 
    ? "Tokenize Now"
    : feature.title === "Staking" 
      ? "Stake Now"
      : feature.title === "Marketplace" 
        ? "View Projects"
        : feature.title === "Quests" 
          ? "Start Quests"
          : "Learn More";

  // Determine the URL based on feature title.
  const url = feature.title === "Staking"
    ? "https://app.crowdlaunch.xyz/stakingv2"
    : feature.title === "Quests"
      ? "https://app.crowdlaunch.xyz/quests"
      : feature.title === "Project Tokenization"
        ? "https://app.crowdlaunch.xyz/tokenize"
        : feature.title === "Marketplace"
          ? "https://app.crowdlaunch.xyz/tokenize"
          : "";

  // Render an icon based on feature title with bigger sizes.
  const renderIcon = () => {
    const iconStyle = {
      fontSize: { xs: '1.5rem', md: '2rem' },
      mb: 2,
      color: darkTheme.palette.primary.main,
    };

    switch (feature.title) {
      case "Project Tokenization":
        return <Box sx={iconStyle}><FiTrendingUp /></Box>;
      case "Staking":
        return <Box sx={iconStyle}><FiDollarSign /></Box>;
      case "Marketplace":
        return <Box sx={iconStyle}><FiShoppingCart /></Box>;
      case "Quests":
        return <Box sx={iconStyle}><FiAward /></Box>;
      default:
        return <Box sx={iconStyle}><FiCheckCircle /></Box>;
    }
  };

  return (
    <Box 
      onClick={handleClick}
      sx={{ 
        cursor: 'pointer', 
        perspective: '1000px',
        mx: { xs: 1, md: 2 },
        width: '100%',
        height: '100%',
        position: 'relative',
        zIndex: 0,
        '&:hover': { zIndex: 10 } // Bring the card forward on hover
      }} 
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '100%', md: '90%' },
          height: '100%',
          transition: 'transform 0.8s',
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front Side */}
        <Box 
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            p: { xs: 2, md: 4 },
            borderRadius: '16px',
            background: `linear-gradient(45deg, ${darkTheme.palette.background.default}, #1a1a2e)`,
            border: '1px solid rgba(255,215,0,0.1)',
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 16px 32px rgba(255,215,0,0.2)'
            }
          }}
        >
          {renderIcon()}
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 2, 
              fontWeight: 700, 
              fontSize: { xs: '1.2rem', md: 'inherit' }
            }}
          >
            {feature.title}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              fontSize: { xs: '0.9rem', md: 'inherit' }
            }}
          >
            {feature.description}
          </Typography>
        </Box>

        {/* Back Side */}
        <Box 
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            background: `linear-gradient(45deg, ${darkTheme.palette.background.default}, #1a1a2e)`,
            border: '1px solid rgba(255,215,0,0.1)',
            borderRadius: '16px',
            p: { xs: 2, md: 4 },
            transform: 'rotateY(180deg)',
          }}
        >
          <Box 
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between', // Top text and bottom button
              alignItems: 'stretch'
            }}
          >
            {/* Top Section: Text */}
            <Box sx={{ textAlign: 'left' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#FFFFFF', 
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', md: 'inherit' }
                }}
              >
                {feature.moreInfo || "More details coming soon."}
              </Typography>
            </Box>
            {/* Bottom Section: Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                href={url} // Use the determined URL
                onClick={(e) => e.stopPropagation()} // Prevent card flip when clicking the button
                sx={{
                  backgroundColor: '#FFD700',
                  color: 'black',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '50px',
                  px: 5,
                  py: 1.5,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0px 8px 16px rgba(0,0,0,0.2)',
                    backgroundColor: '#FFC107'
                  }
                }}
              >
                {actionLabel}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FlippableCard;
